import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../context/AuthContext';
import { GET_CUSTOMER_DATA, UPDATE_CUSTOMER_PROFILE, CHANGE_CUSTOMER_PASSWORD } from '../queries/customerQueries';
import AccountNavigation from './AccountNavigation';
import Loader from './Loader';
import './MyAccount.css';

const AccountProfile = () => {
  const { user, logout, isInitializing } = useAuth();
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isEmailEditable, setIsEmailEditable] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    date_of_birth: '',
    password: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const { data: customerData, loading: customerLoading, error: customerError, refetch: refetchCustomerData } = useQuery(GET_CUSTOMER_DATA);
  const [updateCustomerProfile] = useMutation(UPDATE_CUSTOMER_PROFILE);
  const [changeCustomerPassword] = useMutation(CHANGE_CUSTOMER_PASSWORD);

  if (isInitializing) {
    return (
      <div className="my-account-container">
        <Loader fullScreen={true} size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="my-account-container">
        <div className="my-account-error">
          <h2>Access Denied</h2>
          <p>Please log in to access your account.</p>
        </div>
      </div>
    );
  }

  const handleEditProfile = () => {
    setEditFormData({
      firstname: customerData?.customer?.firstname || '',
      lastname: customerData?.customer?.lastname || '',
      email: customerData?.customer?.email || user.email,
      date_of_birth: customerData?.customer?.date_of_birth || '',
      password: ''
    });
    setIsEditProfileModalOpen(true);
    setSaveError('');
    setSaveSuccess('');
  };

  const handleCancelEdit = () => {
    setIsEditProfileModalOpen(false);
    setIsEmailEditable(false);
    setEditFormData({
      firstname: '',
      lastname: '',
      email: '',
      date_of_birth: '',
      password: ''
    });
    setSaveError('');
    setSaveSuccess('');
  };

  const handleSaveProfile = async () => {
    try {
      setSaveError('');
      setSaveSuccess('');

      if (isEmailEditable && !editFormData.password) {
        setSaveError('Password is required when changing email address.');
        return;
      }

      const updateInput = {
        firstname: editFormData.firstname,
        lastname: editFormData.lastname,
        date_of_birth: editFormData.date_of_birth || null
      };

      if (isEmailEditable) {
        updateInput.email = editFormData.email;
        if (editFormData.password) {
          updateInput.password = editFormData.password;
        }
      }

      await updateCustomerProfile({
        variables: { input: updateInput }
      });

      await refetchCustomerData();
      setIsEditProfileModalOpen(false);
      setIsEmailEditable(false);
      setSaveSuccess('Profile updated successfully!');

      setTimeout(() => setSaveSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setSaveError(error.message || 'Failed to update profile. Please try again.');
    }
  };

  const handleInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOpenChangePassword = () => {
    setIsChangePasswordModalOpen(true);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleCloseChangePassword = () => {
    setIsChangePasswordModalOpen(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePassword = async () => {
    try {
      setPasswordError('');
      setPasswordSuccess('');

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordError('New passwords do not match.');
        return;
      }

      if (passwordData.newPassword.length < 8) {
        setPasswordError('New password must be at least 8 characters long.');
        return;
      }

      await changeCustomerPassword({
        variables: {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        }
      });

      setPasswordSuccess('Password changed successfully!');
      setTimeout(() => {
        setPasswordSuccess('');
        handleCloseChangePassword();
      }, 2000);
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError(error.message || 'Failed to change password. Please check your current password and try again.');
    }
  };

  const renderEditProfileModal = () => {
    if (!isEditProfileModalOpen) return null;

    return (
      <div className="modal-overlay" onClick={handleCancelEdit}>
        <div className="edit-profile-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Edit Profile</h2>
            <button className="modal-close" onClick={handleCancelEdit}>×</button>
          </div>

          <div className="modal-content">
            {saveSuccess && <div className="success-message">{saveSuccess}</div>}
            {saveError && <div className="error-message">{saveError}</div>}

            <div className="profile-edit">
              <div className="profile-grid">
                <div className="form-group">
                  <label htmlFor="firstname">First Name</label>
                  <input
                    type="text"
                    id="firstname"
                    value={editFormData.firstname}
                    onChange={(e) => handleInputChange('firstname', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastname">Last Name</label>
                  <input
                    type="text"
                    id="lastname"
                    value={editFormData.lastname}
                    onChange={(e) => handleInputChange('lastname', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <div className="email-field-header">
                    <label htmlFor="email">Email Address</label>
                    <div className="email-checkbox">
                      <input
                        type="checkbox"
                        id="enableEmailEdit"
                        checked={isEmailEditable}
                        onChange={(e) => setIsEmailEditable(e.target.checked)}
                      />
                      <label htmlFor="enableEmailEdit" className="checkbox-label">
                        Allow email change
                      </label>
                    </div>
                  </div>
                  <input
                    type="email"
                    id="email"
                    value={editFormData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    readOnly={!isEmailEditable}
                    style={{
                      background: isEmailEditable ? 'white' : '#f5f5f5',
                      cursor: isEmailEditable ? 'text' : 'not-allowed'
                    }}
                    placeholder={isEmailEditable ? 'Enter your email address' : ''}
                  />
                  {isEmailEditable && (
                    <small className="email-warning">
                      ⚠️ Changing your email may require verification and could affect your login credentials.
                    </small>
                  )}
                </div>
                {isEmailEditable && (
                  <div className="form-group">
                    <label htmlFor="password">Password (required for email change)</label>
                    <input
                      type="password"
                      id="password"
                      value={editFormData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Enter your current password"
                    />
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="dateOfBirth">Date of Birth</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    value={editFormData.date_of_birth}
                    onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                  />
                </div>
              </div>
              <div className="profile-actions">
                <button className="btn-primary" onClick={handleSaveProfile}>Save Changes</button>
                <button className="btn-secondary" onClick={handleCancelEdit}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderChangePasswordModal = () => {
    if (!isChangePasswordModalOpen) return null;

    return (
      <div className="modal-overlay" onClick={handleCloseChangePassword}>
        <div className="change-password-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Change Password</h2>
            <button className="modal-close" onClick={handleCloseChangePassword}>×</button>
          </div>

          <div className="modal-content">
            {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}
            {passwordError && <div className="error-message">{passwordError}</div>}

            <div className="password-form">
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                  placeholder="Enter your current password"
                />
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                  placeholder="Confirm new password"
                />
              </div>
              <div className="password-actions">
                <button
                  className="btn-primary"
                  onClick={handleChangePassword}
                  disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                >
                  Change Password
                </button>
                <button className="btn-secondary" onClick={handleCloseChangePassword}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="my-account-container">
      <div className="account-header">
        <h1>My Account</h1>
        {customerData?.customer?.firstname ? (
          <p>Welcome back, {customerData.customer.firstname}!</p>
        ) : ('')}
        <button className="logout-btn" onClick={logout}>
          Logout
        </button>
      </div>

      <div className="account-content">
        <AccountNavigation />
        <main className="account-main">
          <div className="account-section">
            {customerLoading ? (
              <div className="loading">Loading profile...</div>
            ) : customerError ? (
              <div className="error">
                Error loading profile: {customerError.message}
                {customerError.graphQLErrors && customerError.graphQLErrors.length > 0 && (
                  <div>
                    <p>GraphQL Errors:</p>
                    {customerError.graphQLErrors.map((err, index) => (
                      <pre key={index}>{JSON.stringify(err, null, 2)}</pre>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="profile-info">
                {saveSuccess && <div className="success-message">{saveSuccess}</div>}
                {saveError && <div className="error-message">{saveError}</div>}

                <div className="profile-main">
                  <div className="profile-display">
                    <p className="profile-name">
                      {customerData?.customer?.firstname || ''} {customerData?.customer?.lastname || ''}
                    </p>
                    <p className="profile-email">{customerData?.customer?.email || user.email}</p>
                    {customerData?.customer?.date_of_birth && (
                      <p className="profile-dob">Born: {new Date(customerData.customer.date_of_birth).toLocaleDateString()}</p>
                    )}
                  </div>
                  <div className="profile-actions">
                    <button className="btn-primary" onClick={handleEditProfile}>Edit Profile</button>
                    <button className="btn-secondary" onClick={handleOpenChangePassword}>Change Password</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {renderEditProfileModal()}
      {renderChangePasswordModal()}
    </div>
  );
};

export default AccountProfile;